import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  useMaterialReactTable,
  MRT_TablePagination,
  MRT_TableContainer,
} from "material-react-table";
import { Box, Tooltip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, } from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MUIDashBoardIcon from "./MUIDashBoardIcon";
import { useDispatch, useSelector } from "react-redux";
import { setColumnOrder } from "../utils/redux/reducer/columnOrderSlice";
import { maskLeadDetails, sensitiveSchemaFields } from "../utils/Common";
import SelectActionMenu from "./SelectActionMenu";
import { GridMoreVertIcon } from "@mui/x-data-grid";

/**
 * CommunicationGrid Component
 * Optimized data grid for communication templates
 * Features: Pagination, column selection, refresh, filters
 * Does NOT include: Sorting, row selection, call/document actions
 */
const CommunicationGrid = ({
  isLoading = false,
  columns = [],
  tabledata = [],
  totalRecords = 0,
  onPageChange,
  clearAllFilter,
  handleRefresh,
  filterChangeCounter = 0,
  timeLineSelected = false,
  tableName = "CommunicationTemplate",
  onRowClick,
  enableRowSelection = false,
  checkedRowIds,
  actionSelect,
  showActionButton = false,
  onClone
}) => {
  const dispatch = useDispatch();
  const savedColumnOrder = useSelector((state) => state.columnOrder[tableName]);

  // Default column order without call actions or row selection
  const defaultColumnOrder = columns.map((c) => c.accessorKey);

  const [columnOrder, setLocalColumnOrder] = useState(
    savedColumnOrder || defaultColumnOrder,
  );

  const [rowSelection, setRowSelection] = useState({});
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMenuRow, setSelectedMenuRow] = useState(null);

  const isLeadDistributionTable = tableName === "LeadDistributionRatio";

  const userDetails = useSelector(
    (state) => state.user.userDetails || { UserDetails: {} },
  );

  const roleId = Number(userDetails?.UserDetails?.roleid);

  const data = useMemo(() => tabledata, [tabledata]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  useEffect(() => {
    dispatch(setColumnOrder({ tableName, columnOrder }));
  }, [columnOrder, dispatch, tableName]);

  const handleColumnOrderChange = (newColumnOrder) => {
    setLocalColumnOrder(newColumnOrder);
  };

  const handleRefreshHandler = () => {
    handleRefresh?.();
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedMenuRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMenuRow(null);
  };

  const handleCloneClick = () => {
    onClone?.(selectedMenuRow.original.leadsjson);
    handleMenuClose();
  };

  // Reset pagination on filter changes
  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: pagination.pageSize,
    });
  }, [filterChangeCounter]);

  // Notify parent on pagination changes
  useEffect(() => {
    onPageChange?.(parseInt(pagination.pageIndex + 1), pagination.pageSize);
  }, [pagination.pageIndex, pagination.pageSize, onPageChange]);

  const handleClearFilters = useCallback(() => {
    setPagination({
      pageIndex: 0,
      pageSize: pagination.pageSize,
    });
    clearAllFilter?.();
  }, [clearAllFilter, pagination.pageSize]);

  /**
   * Render cell with appropriate formatting and masking
   */
  const renderCell = useCallback(
    ({ renderedCellValue, row, columnKey }) => {
      const cellContent =
        (!renderedCellValue || renderedCellValue.trim() === "") &&
        columnKey === "leadname"
          ? "[No Name]"
          : renderedCellValue || "-";

      // Apply sensitive field masking
      if (sensitiveSchemaFields?.includes(columnKey)) {
        return (
          <Tooltip title={cellContent} placement="top">
            <span
              style={{
                color: "#3a36db",
                fontWeight: "inherit",
                cursor: "default",
                fontSize: "inherit",
                textAlign: "start",
              }}
            >
              {roleId === 1 ? cellContent : maskLeadDetails(cellContent)}
            </span>
          </Tooltip>
        );
      }

      // Clickable rows for lead opening
      if (columnKey === "leadsid" && onRowClick) {
        return (
          <Tooltip title={cellContent} placement="top">
            <span
              onClick={() => onRowClick?.(row.original)}
              style={{
                color: "#3A36DB",
                fontWeight: "inherit",
                textDecoration: "none",
                cursor: "pointer",
                maxWidth: "300px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
              }}
            >
              {cellContent}
            </span>
          </Tooltip>
        );
      }

      // Default cell rendering with tooltip
      return (
        <Tooltip title={cellContent} placement="top">
          <div
            style={{
              maxWidth: "300px",
              fontWeight: "inherit",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
              color: "#3A36DB",
            }}
          >
            {cellContent}
          </div>
        </Tooltip>
      );
    },
    [roleId, onRowClick],
  );

  const columnsWithRendering = useMemo(() => {
  const mappedColumns = columns.map((column) => ({
    accessorKey: column.key,
    header: column.header,
    enableSorting: false,
    Cell: ({ renderedCellValue, row }) =>
      renderCell({
        renderedCellValue,
        row,
        columnKey: column.key,
      }),
  }));

  if (tableName === "CommunicationTemplate") {
    if (mappedColumns.length > 0) {
      mappedColumns.push({
        id: "actions",
        header: "",
        size: 50,
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <IconButton onClick={(e) => handleMenuOpen(e, row)}
          style={{ padding: 0 }}>
            <GridMoreVertIcon fontSize="small" />
          </IconButton>
        ),
      });
    }
  }

    return mappedColumns;
  }, [columns, renderCell, tableName]);

  const table = useMaterialReactTable({
    state: {
      rowSelection,
      pagination,
      showSkeletons: isLoading,
      columnOrder,
    },
    manualPagination: true,
    rowCount: totalRecords,
    onPaginationChange: setPagination,
    onColumnOrderChange: handleColumnOrderChange,
    localization: {
      rowsPerPage: (
        <span style={{ color: "#3A36DB", fontWeight: "inherit" }}>
          Total: {totalRecords < 0 ? 0 : totalRecords} show:
        </span>
      ),
    },
    renderEmptyRowsFallback: () => (
      <td colSpan={1000} style={{ textAlign: "center", padding: "2rem" }}>
        <h3>No records to display</h3>
      </td>
    ),
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      showGlobalFilter: false,
      columnPinning: {
        left: ["leadsid"],
      },
      density: "compact",
    },
    enableDensityToggle: false,
    enableRowVirtualization: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: {
          xs: "60vh",
          sm: "60vh",
          md: "60vh",
          lg: timeLineSelected ? "63vh" : "70vh",
          xl: "76vh",
        },
        minHeight: {
          xs: "60vh",
          sm: "60vh",
          md: "60vh",
          lg: timeLineSelected ? "63vh" : "70vh",
          xl: "76vh",
        },
      },
    },
    muiSkeletonProps: {
      animation: "wave",
      height: 25,
    },
    enableSortingRemoval: false,
    enableColumnFilters: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    data,
    columns: columnsWithRendering,
    displayColumnDefOptions: {
      ...(isLeadDistributionTable && {
        "mrt-row-select": {
          header: "",
          size: 50,
          enableColumnActions: false,
          enableSorting: false,
          enableResizing: false,
        },
      }),
    },
    enableColumnOrdering: true,
    // enableRowSelection: false,
    getRowId: (originalRow) => originalRow?.id?.toString(),
    enableRowSelection: isLeadDistributionTable && tabledata.length > 0,
    enableMultiRowSelection: false,
    onRowSelectionChange: isLeadDistributionTable ? setRowSelection : undefined,
    muiPaginationProps: {
      showRowsPerPage: true,
      rowsPerPageOptions: [25, 50, 100, 150, 200],
      variant: "outlined",
      SelectProps: {
        sx: {
          color: "#3F51B5",
          "& .MuiSvgIcon-root": {
            color: "#3F51B5",
            fontWeight: "inherit",
          },
        },
      },
      sx: {
        "& .MuiPaginationItem-root": {
          fontWeight: "inherit",
          color: "#3A36DB",
          "&.Mui-selected": {
            fontWeight: "inherit",
            backgroundColor: "#3A36DB",
            color: "#FFFFFF",
            "&:hover": {
              fontWeight: "inherit",
              backgroundColor: "#3A36DB",
            },
          },
          "&:hover": {
            fontWeight: "550",
            backgroundColor: "#CBCDFB",
          },
        },
        "& .MuiPaginationItem-text": {
          fontWeight: "inherit",
          color: "#3A36DB",
        },
      },
    },
    paginationDisplayMode: "pages",
  });

  useEffect(() => {
    if (!isLeadDistributionTable) return;

    const ids = table.getSelectedRowModel().rows.map((row) => row.original.id);

    setSelectedRowIds(ids);
    checkedRowIds?.(ids);
  }, [rowSelection, table]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        border: "0.5px solid #b3b5b9",
        borderRadius: "5px",
      }}
    >
      {/* Toolbar */}
      <Box
        sx={() => ({
          display: "flex",
          backgroundColor: "#ffffff",
          borderRadius: "4px",
          gap: "16px",
          justifyContent: "flex-end",
          padding: "8px 16px",
          alignItems: "center",
          flexDirection: {
            xs: "column",
            sm: "column",
            md: "column",
            lg: "column",
            xl: "row",
          },
          borderBottom: "0.5px solid #b3b5b9",
        })}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            backgroundColor: "#ffffff",
            marginRight: "20px",
            justifyContent: "center",
            maxHeight: "22px",
          }}
        >
          {/* Refresh Button */}
          <MUIDashBoardIcon
            handleClick={handleRefreshHandler}
            iconName="Refresh"
            fontSize="25px"
            title="Refresh Table Data"
          />

          {/* Clear Filters Button */}
          <MUIDashBoardIcon
            handleClick={handleClearFilters}
            iconName="FilterListOff"
            fontSize="25px"
            title="Reset Filters"
          />

          {isLeadDistributionTable && showActionButton && (
            <SelectActionMenu
              isdisabled={selectedRowIds.length === 0}
              selectedLeads={selectedRowIds}
              onSelectOption={actionSelect}
              forTable="LeadDistributionRatio"
            />
          )}
        </Box>
      </Box>

      {/* Table Container */}
      <MRT_TableContainer table={table} />

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "#ffffff",
          justifyContent: "center",
          width: "100%",
          padding: "8px",
          maxHeight: "50px",
          borderTop: "0.2px solid #B3B5B9",
        }}
      >
        <MRT_TablePagination table={table} />

        {/* Action Menu for Cloning */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
        <MenuItem onClick={handleCloneClick}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>

          <ListItemText>Clone</ListItemText>
        </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default CommunicationGrid;
